import { Environment, Network, RecordSource, Store } from 'relay-runtime';
import {
    composeMiddlewares,
    buildRequestMiddleware,
    authMiddleware,
    settingsMiddleware,
    fetchMiddleware,
    remoteSettingsMiddleware,
    configureFrontendActionLogger,
    frontendTelemetryMiddleware,
} from '@sitemax/smx-components';
import { tokenRefreshMiddleware } from './tokenRefreshMiddleware';
import { graphqlRouterMiddleware } from './graphqlRouterMiddleware';

const DEFAULT_TOKEN = '';

const getApiBaseUrl = (): string => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    if (baseUrl) {
        return baseUrl.replace(/\/+$/, '');
    }

    const protocol =
        (process.env.REACT_APP_API_PROTOCOL || window.location.protocol.replace(':', '')).replace('://', '');
    const port = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';

    return `${protocol}://${window.location.hostname}${port}`;
};

const shouldEnableFrontendActionLogger = (apiBaseUrl: string): boolean => {
    const explicitFlag = process.env.REACT_APP_ENABLE_FRONTEND_ACTION_LOGGER;
    if (explicitFlag === 'true') {
        return true;
    }
    if (explicitFlag === 'false') {
        return false;
    }

    const host = window.location.hostname;
    const isLocalDevHost = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.test');
    if (!isLocalDevHost) {
        return true;
    }

    try {
        return new URL(apiBaseUrl).hostname === host;
    } catch {
        return false;
    }
};

const createEnvironment = (token: string) => {
    const apiBaseUrl = getApiBaseUrl();

    configureFrontendActionLogger({
        enabled: shouldEnableFrontendActionLogger(apiBaseUrl),
        apiBaseUrl,
        tokenProvider: async () => token || localStorage.getItem('accessToken'),
    });

    const middlewareChain = composeMiddlewares([
        buildRequestMiddleware({
            graphqlUrl: apiBaseUrl,
            graphqlPathApi: 'api/v1',
        }),
        graphqlRouterMiddleware,
        authMiddleware(token),
        settingsMiddleware,
        remoteSettingsMiddleware,
        frontendTelemetryMiddleware,
        tokenRefreshMiddleware,
        fetchMiddleware,
    ]);

    return new Environment({
        network: Network.create(middlewareChain),
        store: new Store(new RecordSource()),
    });
};

const dynamicEnvironment = {
    token: DEFAULT_TOKEN,
    environment: createEnvironment(DEFAULT_TOKEN),
};

export const getEnvironment = (token: string): Environment => {
    if (token && dynamicEnvironment.token === token) {
        return dynamicEnvironment.environment;
    }

    dynamicEnvironment.token = token;
    dynamicEnvironment.environment = createEnvironment(token);

    return dynamicEnvironment.environment;
};
